import { useOs } from '@wppopen/react'
import { jwtDecode } from 'jwt-decode'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'

import { bvaAPI, matchandthriveApi, facadeApi } from 'api'
import { ApiInstance } from 'api/common/createApi'
import { useAZToken } from 'hooks/useAZToken'
import { isTokenExpired } from 'utils/general'

const apiInstances = [bvaAPI, facadeApi, matchandthriveApi]

const getInterceptor = (instance: ApiInstance, azAccessToken: string, wppToken: string) =>
  instance.client.interceptors.request.use(
    config => {
      config.headers.Authorization = `Bearer ${instance === facadeApi ? wppToken : azAccessToken}`
      config.headers.Client = 'playground-dev-fe'
      return config
    },
    error => Promise.reject(error),
  )

export const ApiProviderAlphaZulu = ({ children }: PropsWithChildren) => {
  const { osApi } = useOs()
  const [interceptorsDone, setInterceptorsDone] = useState(false)
  const { azAccessToken, removeToken, refreshToken } = useAZToken(osApi.getAccessToken())

  useEffect(() => {
    if (!azAccessToken) return

    const interceptors = apiInstances.map(instance => {
      const requestInterceptor = getInterceptor(instance, azAccessToken, osApi.getAccessToken())

      if (instance === bvaAPI) {
        instance.client.interceptors.response.use(
          response => response,
          error => {
            try {
              const decodedToken = jwtDecode(azAccessToken)
              if (error.response?.status === 401 && isTokenExpired(decodedToken?.exp)) {
                removeToken()
                refreshToken()
                error.config._retry = true
                return instance.client(error.config)
              }
            } catch {
              // Ignore malformed token responses and return the original error.
            }
            return Promise.reject(error)
          },
        )
      }

      return requestInterceptor
    })

    setInterceptorsDone(true)
    return () => {
      interceptors.forEach((interceptor, index) => apiInstances[index].client.interceptors.request.eject(interceptor))
    }
    // Tokens are refreshed by useAZToken; re-register when the access token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [azAccessToken])

  const isReady = useMemo(
    () => Boolean(azAccessToken && osApi.getAccessToken() && interceptorsDone),
    [azAccessToken, interceptorsDone, osApi],
  )

  return isReady ? <>{children}</> : null
}
